import { useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LANDING_HTML } from "@/landing/landing-html";
import { checkAuth, loginRedirectUrl } from "@/auth/useAuth";
import "@/landing/assets/tokens.css";
import "@/landing/assets/landing.css";

const apiBase = (): string => (import.meta.env.VITE_API_URL ?? "") as string;

type ThemeMode = "light" | "dark";
const THEME_KEY = "artie-landing-theme";

/** Resolve the landing's initial light/dark mode. The dispatch design is
 *  dark-native, so we default to dark — but honor a saved choice first, and an
 *  explicit `prefers-color-scheme: light` on the very first visit. */
function getInitialMode(): ThemeMode {
  try {
    // An explicit ?theme=light|dark wins (deep-linkable) and becomes the saved pref.
    const q = new URLSearchParams(window.location.search).get("theme");
    if (q === "light" || q === "dark") return q;
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    /* localStorage blocked (private mode) — fall through to system/default */
  }
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

/** Full-page redirect straight to the BFF sign-in (which 302s to Keycloak).
 *  The landing has no password form of its own, so every "Login"/CTA goes
 *  here directly — no intermediate "Continue to sign in" dialog. */
function redirectToSignIn(redirectTo = "/workspace") {
  window.location.assign(`${loginRedirectUrl(apiBase())}?from=${encodeURIComponent(redirectTo)}`);
}

/**
 * Public marketing landing — ports artie-demo/index.html verbatim as a
 * raw HTML string, mounts the demo's icons.js + landing.js once on
 * insert so the scroll-reveal + terminal animation work. The "Book a
 * demo" / "Sign in" CTAs redirect straight to the BFF /auth/login (which
 * 302s to Keycloak) — no intermediate dialog. Authenticated users hitting `/` are bounced
 * to the workspace by an effect — no flash because the redirect runs
 * before the HTML mount.
 */
export function Landing() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rootRef = useRef<HTMLDivElement>(null);

  // Apply the theme as a plain DOM attribute BEFORE paint (no flash). Theme is
  // kept entirely on the DOM — never React state — so toggling never re-renders
  // the dangerouslySetInnerHTML container. A re-render would re-apply innerHTML
  // and wipe the hub/charts/terminal that landing.js builds imperatively,
  // blanking the page.
  useLayoutEffect(() => {
    rootRef.current?.setAttribute("data-mode", getInitialMode());
  }, []);

  // Already signed in (live cookie session) → straight to /workspace.
  useEffect(() => {
    let cancelled = false;
    const apiBase = (import.meta.env.VITE_API_URL ?? "") as string;
    checkAuth(apiBase).then((user) => {
      if (!cancelled && user) navigate("/workspace", { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // ?login=1 (RequireAuth bounce for an unauthenticated user) → go straight
  // to sign-in. No modal; the landing has nothing to collect first.
  useEffect(() => {
    if (params.get("login") === "1") redirectToSignIn();
  }, [params]);

  // The MUI theme pins `body { overflow: hidden }` because the workspace
  // app has its own scroll containers. The landing is a tall public page
  // and needs the natural document scroll for anchor links to work. Toggle
  // it just on this route.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    // Smooth scroll for native anchor navigation on browsers that honor it.
    const prevScroll = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.scrollBehavior = prevScroll;
    };
  }, []);

  // After raw HTML mounts, load the demo's icons.js + landing.js as side-
  // effect scripts and intercept the # CTAs to open the modal instead of
  // scrolling to nowhere.
  useEffect(() => {
    let cancelled = false;
    const root = rootRef.current;
    if (!root) return;

    // landing.js reads window.Charts (from charts.js), window.DATA +
    // window.CHARTS (from data.js), and the global `icon()` helper (from
    // icons.js). Load each as a real <script> so the IIFEs export to
    // window, in dependency order.
    Promise.all([
      import("@/landing/assets/icons.js?url"),
      import("@/landing/assets/charts.js?url"),
      import("@/landing/assets/data.js?url"),
      import("@/landing/assets/landing.js?url"),
    ]).then(([iconsMod, chartsMod, dataMod, landingMod]) => {
      if (cancelled) return;
      const loadScript = (src: string): Promise<void> =>
        new Promise((resolve) => {
          const s = document.createElement("script");
          s.src = src;
          s.async = false;
          s.onload = () => resolve();
          s.onerror = () => resolve(); // best-effort; don't block landing
          document.body.appendChild(s);
        });
      (async () => {
        await loadScript(iconsMod.default);
        await loadScript(chartsMod.default);
        await loadScript(dataMod.default);
        await loadScript(landingMod.default);
      })();
    });

    // Intercept ANY CTA that would leave the landing (or jump to #cta /
    // an industry sub-page) and open the login modal instead. The user
    // must be signed in to reach the app, so every entry path funnels
    // through the modal. Pure in-page anchors (#how, #try, #control) keep
    // their native scroll-to behavior.
    const IN_PAGE_ANCHORS = new Set([
      "#how", "#try", "#control",
    ]);
    const handler = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const a = t.closest("a");
      if (!(a instanceof HTMLAnchorElement)) return;
      const href = a.getAttribute("href") ?? "";
      // Brandmark → we're already on the single-page landing, so just scroll to
      // the top. Don't navigate: the old href="index.html" (and a full "/" reload)
      // bounced the SPA to a route that doesn't exist and rendered a blank page.
      if (href === "index.html" || href === "/" || href === "") {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      // Pure in-page scroll anchors — keep them
      if (IN_PAGE_ANCHORS.has(href)) return;
      // External links (mailto, http(s) elsewhere) — let them open
      // in a new tab naturally.
      if (a.target === "_blank") return;
      if (/^https?:\/\//i.test(href) && !href.startsWith(window.location.origin)) {
        return; // external — let it navigate normally
      }
      // Everything else (CTAs, industry pages, /login, #login, #cta) →
      // redirect straight to sign-in (no intermediate dialog).
      e.preventDefault();
      redirectToSignIn();
    };
    root.addEventListener("click", handler);

    // Wire the nav light/dark toggle via DELEGATION on the root, flipping the
    // data-mode attribute directly (no React state → no re-render → the
    // landing.js-built content is never wiped). Persist the choice.
    const onToggle = (e: Event) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest(".theme-toggle")) return;
      const next = root.getAttribute("data-mode") === "light" ? "dark" : "light";
      root.setAttribute("data-mode", next);
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        /* ignore write failures (private mode) */
      }
    };
    root.addEventListener("click", onToggle);

    // Login is a declarative <a href="#login"> in the nav markup — the click
    // handler above already routes #login → sign-in. (It used to be injected
    // imperatively, but that DOM node didn't survive theme re-renders, so the
    // Login button vanished on toggle. Declarative markup is theme-stable.)

    // The in-page "Book a demo" / "See it live →" CTAs now route to the
    // login modal instead of external signup links (registration now
    // happens through Keycloak's "Create account" on the sign-in page).

    // Native anchor-scroll on hard load: the SPA mounts AFTER the browser
    // has resolved (and failed) the URL hash, so re-scroll once the demo
    // HTML is in the DOM. Runs every Landing mount — cheap, no-op when
    // there's no hash.
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const id = hash.slice(1);
      // Wait two frames: one for innerHTML commit, one for layout settle
      // (landing.js mutates DOM on mount).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const target = document.getElementById(id) ?? root.querySelector(`#${CSS.escape(id)}`);
          if (target) {
            const rect = target.getBoundingClientRect();
            const top = rect.top + window.scrollY - 12;
            window.scrollTo({ top, behavior: "smooth" });
          }
        });
      });
    }
    // Smooth-scroll for IN_PAGE_ANCHORS via capture-phase root listener.
    // Capture ensures we win over any later-attached listeners from the
    // demo's landing.js (e.g. the Industries dropdown click).
    const anchorScroll = (ev: Event) => {
      const me = ev as MouseEvent;
      // Don't hijack ctrl/cmd-click (open in new tab) or middle-click.
      if (me.ctrlKey || me.metaKey || me.shiftKey || me.button !== 0) return;
      const t = me.target;
      if (!(t instanceof Element)) return;
      const a = t.closest("a");
      if (!(a instanceof HTMLAnchorElement)) return;
      const href = a.getAttribute("href") ?? "";
      if (!IN_PAGE_ANCHORS.has(href)) return;
      const id = href.slice(1);
      // Search the whole document — sometimes the anchor target sits
      // outside the rootRef subtree (e.g. when MUI portals or future
      // wrappers move content).
      const target = document.getElementById(id) ?? root.querySelector(`#${CSS.escape(id)}`);
      if (!target) return;
      ev.preventDefault();
      ev.stopPropagation();
      // Compute absolute Y to scroll the document explicitly. Some
      // browsers ignore scrollIntoView when an ancestor has overflow set
      // — explicit window.scrollTo is more reliable.
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.scrollY - 12;
      window.scrollTo({ top, behavior: "smooth" });
      history.replaceState(null, "", href);
    };
    root.addEventListener("click", anchorScroll, true);
    // Cleanup tracked via closure capture (returned below).
    (root as Element & { _anchorScroll?: EventListener })._anchorScroll = anchorScroll;

    return () => {
      cancelled = true;
      root.removeEventListener("click", handler);
      root.removeEventListener("click", onToggle);
      const r = root as Element & { _anchorScroll?: EventListener };
      if (r._anchorScroll) {
        root.removeEventListener("click", r._anchorScroll, true);
        delete r._anchorScroll;
      }
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="canvas"
      dangerouslySetInnerHTML={{ __html: LANDING_HTML }}
    />
  );
}
